<script setup lang="ts">
import OperatorAccountMenu from './OperatorAccountMenu.vue';
import BoxWithdrawal from './BoxWithdrawal.vue';
import ProviderBoxAccess from './ProviderBoxAccess.vue';
import ManualPreparation, { type PreparationOption } from './ManualPreparation.vue';
import BoxSetupTutorial from './BoxSetupTutorial.vue';
import ImmediateChoice from './ImmediateChoice.vue';
import OrderReference from './OrderReference.vue';
import TravelerCommentReview from './TravelerCommentReview.vue';
import { commentBlocks, type CommentReview } from '../order-management';
import { useOperatorTabHistory } from '../operator-tab-history';
import { computed, nextTick, onMounted, ref } from 'vue';
import ReservationSummary from './ReservationSummary.vue';
import type { ReservationSummary as Reservation } from '../reservation';
import OrderManagement from './OrderManagement.vue';
import OfferGallery from './OfferGallery.vue';
import { cancellationBlocks, type Cancellation, type Modification } from '../order-management';
import OperationalNavigation from './OperationalNavigation.vue';
import preparationImage from '../assets/hero-snack-flow.png';

type Product = { name: string; quantity: number };
type Offer = {
  offerId: string;
  displayName: string;
  products?: Product[];
  photoDataUrl?: string;
};
type ProviderException = {
  providerId: string;
  proposedAlternative: string | null;
  refusalReason: string | null;
  reportedAt: string;
};
type Preparation = {
  deliveryRequestKind: 'CURRENT_LOGISTICS';
  deliveryRequestId: string;
  deliveryLifecycleState:
    | 'RECEIVED'
    | 'MODIFICATION_PROPOSEE'
    | 'REFUSEE'
    | 'PREPARING'
    | 'READY'
    | 'DELIVERED'
    | 'CANCELLED';
  cancellation?: Cancellation;
  modifications?: Modification[];
  prepaymentStatus?: 'PAID' | 'NOT_PREPAID' | 'AUTHORIZED' | 'EXPIRED' | 'CANCELED';
  canChangeOffer?: boolean;
  deliveryCapturePending?: boolean;
  selectedBoxOfferId: string;
  selectedBoxOfferDisplayNameSnapshot: string;
  apartmentName: string;
  travelerName: string;
  travelerComment?: string;
  travelerCommentReview?: CommentReview | null;
  reservation?: Reservation;
  deliveryDeadlineAt: string;
  absentEntryAuthorized: boolean;
  providerException: ProviderException | null;
  assignedBox?: { displayName: string; publicQrToken: string };
  preparedByProviderId?: string | null;
  temporaryPreparation?: { displayName: string };
  configuredAssignment?: boolean;
  availablePhysicalBoxes?: Array<{ boxId: string; displayName: string }>;
};
type OperationalApartment = {
  apartment: string;
  city: string | null;
  owner: string | null;
  slotNumber: number;
  totalSlots: number;
  deliveryRequestId: string | null;
  initialOfferId: string | null;
  initialOfferDisplayName: string | null;
  assignmentEligibility: 'ELIGIBLE';
  availablePhysicalBoxes: Array<{ boxId: string; displayName: string }>;
};
type ProviderBox = {
  lockCode?: string | null;
  withdrawalRequestId?: string | null;
  lockRenewal?: { renewalAt: string | null; renewalDue: boolean };
  boxId: string;
  displayName: string;
  availabilityState: 'AVAILABLE' | 'ASSIGNED_OR_IN_USE';
  conditionState: 'ACTIVE' | 'THEFT' | 'BREAKAGE';
  currentDeliveryRequestId: string | null;
  travelerConsumed: boolean;
  replacementApartmentName?: string;
  replacementOfferDisplayName?: string;
  replacementAcknowledged?: boolean;
  currentDeliveryAssignment?: {
    apartmentName?: string;
    deliveryLifecycleState?: string;
    configuredAssignmentSlotNumber?: number;
  };
};
type ProductDraft = {
  expectedName: string;
  expectedQuantity: number;
  actualName: string;
  actualQuantity: number;
};

const activeTab = useOperatorTabHistory(['home', 'orders', 'boxes', 'catalog', 'more']);
const selectedOfferId = ref('');
const selectedCatalogOffer = computed(() =>
  offers.value.find((o) => o.offerId === selectedOfferId.value),
);
const workflow = ref('RECEIVED');
const workflowNavigation = ref<{ scrollLeft: number } | null>(null);
async function selectNavigation(tab: string): Promise<void> {
  activeTab.value = tab;
  if (tab === 'orders') {
    workflow.value = 'RECEIVED';
    await nextTick();
    if (workflowNavigation.value) workflowNavigation.value.scrollLeft = 0;
  }
}
const viewStale = ref(false);
const navigation = [
  { id: 'home', label: 'Aujourd’hui' },
  { id: 'orders', label: 'Commandes' },
  { id: 'boxes', label: 'Boxes' },
  { id: 'catalog', label: 'Offres' },
  { id: 'more', label: 'Plus' },
];
function openWorkflow(step: string): void {
  activeTab.value = 'orders';
  workflow.value = step;
}
const offers = ref<Offer[]>([]);
const preparations = ref<Preparation[]>([]);
const boxes = ref<ProviderBox[]>([]);
function currentBoxFor(request: Preparation): ProviderBox | undefined {
  if (loading.value || viewStale.value) return undefined;
  return boxes.value.find((box) => box.currentDeliveryRequestId === request.deliveryRequestId);
}
const providerId = ref('');
const productDrafts = ref<Record<string, ProductDraft[]>>({});
const modifying = ref<Record<string, boolean>>({});
const loading = ref(true);
const saving = ref(false);
const error = ref('');
const actionError = ref('');
const success = ref('');
const lockBoxId = ref('');
const newLockCode = ref('');
const physicalLockChanged = ref(false);
const apartmentsWithoutActiveBox = ref<OperationalApartment[]>([]);
const manualPreparationOptions = ref<PreparationOption[]>([]);
async function afterManualPreparation(): Promise<void> {
  await load();
  if (!viewStale.value) {
    activeTab.value = 'orders';
    workflow.value = 'PREPARING';
    success.value = 'Préparation hors commande créée.';
  }
}
const providerScopes = ref({
  cities: [] as string[],
  apartments: [] as string[],
  owners: [] as string[],
});
const assignmentBoxIds = ref<Record<string, string>>({});
const reportingBoxIds = ref<Record<string, boolean>>({});
const missingBoxAlerts = ref<Record<string, boolean>>({});
const alternativeDrafts = ref<Record<string, string>>({});
const alternativeDates = ref<Record<string, string>>({});
const refusalDrafts = ref<Record<string, string>>({});
const exceptionSubmitting = ref<Record<string, boolean>>({});

const activePreparations = computed(() =>
  preparations.value.filter(
    (request) => request.deliveryLifecycleState === 'PREPARING' && !cancellationBlocks(request),
  ),
);
const receivedPreparations = computed(() =>
  preparations.value.filter(
    (request) => request.deliveryLifecycleState === 'RECEIVED' && !cancellationBlocks(request),
  ),
);
const readyDeliveries = computed(() =>
  preparations.value.filter(
    (request) =>
      request.deliveryLifecycleState === 'READY' &&
      !cancellationBlocks(request) &&
      !request.deliveryCapturePending,
  ),
);
const exceptionPreparations = computed(() =>
  preparations.value.filter((request) =>
    ['MODIFICATION_PROPOSEE', 'REFUSEE'].includes(request.deliveryLifecycleState),
  ),
);
function needsAttention(request: Preparation): boolean {
  return (
    request.deliveryCapturePending === true ||
    ['MODIFICATION_PROPOSEE', 'REFUSEE'].includes(request.deliveryLifecycleState) ||
    (!['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState) &&
      commentBlocks(request)) ||
    Boolean(
      request.modifications?.some(
        (modification) =>
          !['APPLIED', 'REJECTED'].includes(modification.status) ||
          (modification.status === 'REJECTED' &&
            !['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)),
      ),
    ) ||
    Boolean(
      request.cancellation &&
      !['REJECTED', 'REFUNDED_SIMULATED', 'REFUNDED', 'SETTLED'].includes(
        request.cancellation.status,
      ),
    )
  );
}
const attentionPreparations = computed(() => preparations.value.filter(needsAttention));
const workflowSteps = computed(() => [
  { id: 'RECEIVED', label: 'Reçues', count: receivedPreparations.value.length },
  { id: 'PREPARING', label: 'Préparer', count: activePreparations.value.length },
  { id: 'READY', label: 'Livrer', count: readyDeliveries.value.length },
  {
    id: 'DELIVERED',
    label: 'Terminées',
    count: preparations.value.filter(
      (request) => request.deliveryLifecycleState === 'DELIVERED',
    ).length,
  },
  { id: 'ALERTS', label: 'Alertes', count: attentionPreparations.value.length },
]);
const dailyGroups = computed(() =>
  [
    {
      id: 'ALERTS',
      label: 'Demandes à contrôler',
      hint: 'Commentaires, modifications et annulations',
      requests: attentionPreparations.value,
    },
    {
      id: 'RECEIVED',
      label: 'Boxes à affecter',
      hint: 'Choisir une box pour démarrer la préparation',
      requests: receivedPreparations.value.filter((r) => !needsAttention(r)),
    },
    {
      id: 'PREPARING',
      label: 'Préparations en cours',
      hint: 'Vérifier le contenu et confirmer la préparation',
      requests: activePreparations.value.filter((r) => !needsAttention(r)),
    },
    {
      id: 'READY',
      label: 'Livraisons à confirmer',
      hint: 'Confirmer uniquement après la livraison',
      requests: readyDeliveries.value.filter((r) => !needsAttention(r)),
    },
  ].map((group) => ({
    ...group,
    requests: [...group.requests].sort(
      (a, b) => Date.parse(a.deliveryDeadlineAt) - Date.parse(b.deliveryDeadlineAt),
    ),
  })),
);
const ordersWithoutBox = computed(() =>
  receivedPreparations.value.filter((request) => !request.availablePhysicalBoxes?.length),
);
const workflowCountsReady = computed(() => !loading.value && !viewStale.value && !error.value);
const installedBoxes = computed(() =>
  boxes.value.filter(
    (box) =>
      box.availabilityState === 'ASSIGNED_OR_IN_USE' &&
      box.currentDeliveryAssignment?.deliveryLifecycleState === 'DELIVERED' &&
      Boolean(box.currentDeliveryAssignment.apartmentName?.trim()),
  ),
);
const inProgressBoxes = computed(() =>
  boxes.value.filter(
    (box) =>
      box.availabilityState === 'ASSIGNED_OR_IN_USE' &&
      ['PREPARING', 'READY'].includes(
        String(box.currentDeliveryAssignment?.deliveryLifecycleState),
      ) &&
      Boolean(box.currentDeliveryAssignment?.apartmentName?.trim()),
  ),
);
const availableBoxes = computed(() =>
  boxes.value.filter(
    (box) =>
      box.conditionState === 'ACTIVE' &&
      box.availabilityState === 'AVAILABLE' &&
      !box.travelerConsumed,
  ),
);
const replacementBoxes = computed(() =>
  boxes.value.filter(
    (box) =>
      box.availabilityState === 'AVAILABLE' &&
      box.currentDeliveryRequestId === null &&
      box.travelerConsumed &&
      box.replacementAcknowledged !== true &&
      Boolean(box.replacementApartmentName),
  ),
);
const operationalScopeEmpty = computed(
  () =>
    installedBoxes.value.length === 0 &&
    inProgressBoxes.value.length === 0 &&
    apartmentsWithoutActiveBox.value.length === 0 &&
    replacementBoxes.value.length === 0,
);
const operationalCardCount = computed(
  () =>
    installedBoxes.value.length +
    inProgressBoxes.value.length +
    apartmentsWithoutActiveBox.value.length +
    replacementBoxes.value.length,
);
const operationalCardNumbering = computed(() => {
  const positions = [
    ...installedBoxes.value.map((box) => ({
      key: `position:${box.boxId}`,
      apartment: box.currentDeliveryAssignment?.apartmentName ?? '',
      slot: box.currentDeliveryAssignment?.configuredAssignmentSlotNumber ?? null,
    })),
    ...inProgressBoxes.value.map((box) => ({
      key: `position:${box.boxId}`,
      apartment: box.currentDeliveryAssignment?.apartmentName ?? '',
      slot: box.currentDeliveryAssignment?.configuredAssignmentSlotNumber ?? null,
    })),
    ...replacementBoxes.value.map((box) => ({
      key: `position:${box.boxId}`,
      apartment: box.replacementApartmentName ?? '',
      slot: null,
    })),
    ...apartmentsWithoutActiveBox.value.map((item) => ({
      key: `position:${item.apartment}:${item.slotNumber}`,
      apartment: item.apartment,
      slot: item.slotNumber,
    })),
  ];
  const groups = new Map<string, typeof positions>();
  for (const position of positions) {
    groups.set(position.apartment, [...(groups.get(position.apartment) ?? []), position]);
  }
  const labels = new Map<string, string>();
  for (const group of groups.values()) {
    if (group.length <= 1) continue;
    const used = new Set<number>();
    const sorted = [...group].sort((left, right) => left.key.localeCompare(right.key));
    for (const position of sorted) {
      const requested = position.slot;
      if (
        Number.isSafeInteger(requested) &&
        Number(requested) >= 1 &&
        Number(requested) <= group.length &&
        !used.has(Number(requested))
      ) {
        used.add(Number(requested));
        labels.set(position.key, ` ${requested}/${group.length}`);
      }
    }
    for (const position of sorted) {
      if (labels.has(position.key)) continue;
      const slot = Array.from({ length: group.length }, (_, index) => index + 1).find(
        (candidate) => !used.has(candidate),
      );
      if (slot) {
        used.add(slot);
        labels.set(position.key, ` ${slot}/${group.length}`);
      }
    }
  }
  return labels;
});
const productCatalog = computed(() => {
  const products = new Map<string, Product>();
  for (const offer of offers.value) {
    for (const product of offer.products ?? []) products.set(product.name, product);
  }
  return [...products.values()].sort((left, right) =>
    left.name.localeCompare(right.name, 'fr'),
  );
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isProviderException(value: unknown): value is ProviderException {
  return (
    isRecord(value) &&
    typeof value.providerId === 'string' &&
    (value.proposedAlternative === null || typeof value.proposedAlternative === 'string') &&
    (value.refusalReason === null || typeof value.refusalReason === 'string') &&
    typeof value.reportedAt === 'string'
  );
}

function isVerifiedAlternativeProjection(
  value: unknown,
  deliveryRequestId: string,
  proposal: string,
): boolean {
  return (
    isRecord(value) &&
    value.deliveryRequestId === deliveryRequestId &&
    value.deliveryLifecycleState === 'MODIFICATION_PROPOSEE' &&
    isProviderException(value.providerException) &&
    value.providerException.proposedAlternative === proposal
  );
}

function formatDeliveryDeadline(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function productsFor(request: Preparation): Product[] {
  return (
    offers.value.find((offer) => offer.offerId === request.selectedBoxOfferId)?.products ?? []
  );
}

function offerPhotoFor(request: Preparation): string {
  return (
    offers.value.find((offer) => offer.offerId === request.selectedBoxOfferId)?.photoDataUrl ??
    preparationImage
  );
}

function isConfiguredInstalledBox(box: ProviderBox): boolean {
  return (
    preparations.value.find(
      (request) => request.deliveryRequestId === box.currentDeliveryRequestId,
    )?.configuredAssignment === true
  );
}

function installedApartmentClass(box: ProviderBox): string {
  return isConfiguredInstalledBox(box)
    ? 'provider-apartment-installed'
    : 'provider-apartment-delivered';
}

function installedApartmentStatus(box: ProviderBox): string {
  return isConfiguredInstalledBox(box)
    ? 'Box installée · non commandée'
    : 'Box commandée · livrée';
}

function receivedFor(apartment: OperationalApartment): Preparation | undefined {
  return receivedPreparations.value.find(
    (request) =>
      request.deliveryRequestId === apartment.deliveryRequestId ||
      (apartment.deliveryRequestId === null && request.apartmentName === apartment.apartment),
  );
}

function eligibleBoxesFor(apartment: OperationalApartment): Array<{
  boxId: string;
  displayName: string;
}> {
  const request = receivedFor(apartment);
  if (request) return request.availablePhysicalBoxes ?? [];
  return apartment.availablePhysicalBoxes;
}

function verificationInputId(requestId: string, index: number): string {
  return `provider-verification-quantity-${requestId}-${index}`;
}

function initializeDrafts(): void {
  productDrafts.value = Object.fromEntries(
    preparations.value.map((request) => [
      request.deliveryRequestId,
      productsFor(request).map((product) => ({
        expectedName: product.name,
        expectedQuantity: product.quantity,
        actualName: product.name,
        actualQuantity: product.quantity,
      })),
    ]),
  );
}

async function load({
  propagateError = false,
}: { propagateError?: boolean } = {}): Promise<void> {
  loading.value = true;
  error.value = '';
  actionError.value = '';
  try {
    const [requestsResponse, offersResponse, boxesResponse, scopeResponse] = await Promise.all([
      globalThis.fetch('/provider/api/delivery-requests', { credentials: 'include' }),
      globalThis.fetch('/provider/api/box-offers', { credentials: 'include' }),
      globalThis.fetch('/provider/api/boxes', { credentials: 'include' }),
      globalThis.fetch('/provider/api/operational-scope', { credentials: 'include' }).catch(
        () =>
          new globalThis.Response(
            JSON.stringify({
              scopes: { cities: [], apartments: [], owners: [] },
              apartmentsWithoutActiveBox: [],
            }),
            { status: 200 },
          ),
      ),
    ]);
    const requestsBody: unknown = await requestsResponse.json();
    const offersBody: unknown = await offersResponse.json();
    const boxesBody: unknown = await boxesResponse.json();
    const scopeBody: unknown = await scopeResponse.json();
    if (
      !requestsResponse.ok ||
      !offersResponse.ok ||
      !boxesResponse.ok ||
      !scopeResponse.ok ||
      !isRecord(requestsBody) ||
      !Array.isArray(requestsBody.deliveryRequests) ||
      !isRecord(offersBody) ||
      !Array.isArray(offersBody.boxOffers) ||
      !isRecord(boxesBody) ||
      !Array.isArray(boxesBody.boxes) ||
      !isRecord(scopeBody) ||
      typeof requestsBody.providerId !== 'string'
    ) {
      throw new Error('Données de préparation indisponibles.');
    }
    providerId.value = requestsBody.providerId;
    preparations.value = requestsBody.deliveryRequests.filter(
      (request): request is Preparation =>
        isRecord(request) &&
        request.deliveryRequestKind === 'CURRENT_LOGISTICS' &&
        typeof request.deliveryRequestId === 'string' &&
        [
          'RECEIVED',
          'MODIFICATION_PROPOSEE',
          'REFUSEE',
          'PREPARING',
          'READY',
          'DELIVERED',
        ].includes(String(request.deliveryLifecycleState)) &&
        typeof request.selectedBoxOfferId === 'string' &&
        typeof request.selectedBoxOfferDisplayNameSnapshot === 'string' &&
        typeof request.apartmentName === 'string' &&
        typeof request.travelerName === 'string' &&
        typeof request.deliveryDeadlineAt === 'string' &&
        typeof request.absentEntryAuthorized === 'boolean' &&
        (request.providerException === null ||
          isProviderException(request.providerException)) &&
        (request.availablePhysicalBoxes === undefined ||
          (Array.isArray(request.availablePhysicalBoxes) &&
            request.availablePhysicalBoxes.every(
              (box) =>
                isRecord(box) &&
                typeof box.boxId === 'string' &&
                typeof box.displayName === 'string',
            ))),
    );
    offers.value = offersBody.boxOffers.filter(
      (offer): offer is Offer =>
        isRecord(offer) &&
        typeof offer.offerId === 'string' &&
        typeof offer.displayName === 'string' &&
        (offer.products === undefined || Array.isArray(offer.products)) &&
        (offer.photoDataUrl === undefined || typeof offer.photoDataUrl === 'string'),
    );
    boxes.value = boxesBody.boxes
      .filter(
        (box): box is Omit<ProviderBox, 'travelerConsumed'> & { travelerConsumed?: boolean } =>
          isRecord(box) &&
          typeof box.boxId === 'string' &&
          typeof box.displayName === 'string' &&
          ['AVAILABLE', 'ASSIGNED_OR_IN_USE'].includes(String(box.availabilityState)) &&
          ['ACTIVE', 'THEFT', 'BREAKAGE'].includes(String(box.conditionState ?? 'ACTIVE')) &&
          (box.travelerConsumed === undefined || typeof box.travelerConsumed === 'boolean') &&
          (box.replacementApartmentName === undefined ||
            typeof box.replacementApartmentName === 'string') &&
          (box.replacementAcknowledged === undefined ||
            typeof box.replacementAcknowledged === 'boolean') &&
          (box.currentDeliveryRequestId === null ||
            typeof box.currentDeliveryRequestId === 'string'),
      )
      .map((box) => ({
        ...box,
        conditionState: box.conditionState ?? 'ACTIVE',
        travelerConsumed: box.travelerConsumed === true,
        lockCode:
          typeof box.lockCode === 'string' && /^\d{3}$/.test(box.lockCode)
            ? box.lockCode
            : null,
      }));
    manualPreparationOptions.value = Array.isArray(scopeBody.preparationOptions)
      ? scopeBody.preparationOptions.filter(
          (item): item is PreparationOption =>
            isRecord(item) &&
            typeof item.apartment === 'string' &&
            Array.isArray(item.offers) &&
            Array.isArray(item.availablePhysicalBoxes),
        )
      : [];
    apartmentsWithoutActiveBox.value = Array.isArray(scopeBody.apartmentsWithoutActiveBox)
      ? scopeBody.apartmentsWithoutActiveBox
          .filter(
            (item): item is Record<string, unknown> =>
              isRecord(item) &&
              typeof item.apartment === 'string' &&
              (item.city === null || typeof item.city === 'string') &&
              (item.owner === null || typeof item.owner === 'string'),
          )
          .map((item) => {
            const matchingRequest = receivedPreparations.value.find(
              (request) => request.apartmentName === item.apartment,
            );
            return {
              apartment: String(item.apartment),
              city: typeof item.city === 'string' ? item.city : null,
              owner: typeof item.owner === 'string' ? item.owner : null,
              slotNumber: Number.isSafeInteger(item.slotNumber) ? Number(item.slotNumber) : 1,
              totalSlots: Number.isSafeInteger(item.totalSlots) ? Number(item.totalSlots) : 1,
              deliveryRequestId:
                typeof item.deliveryRequestId === 'string'
                  ? item.deliveryRequestId
                  : (matchingRequest?.deliveryRequestId ?? null),
              initialOfferId:
                typeof item.initialOfferId === 'string'
                  ? item.initialOfferId
                  : (matchingRequest?.selectedBoxOfferId ?? null),
              initialOfferDisplayName:
                typeof item.initialOfferDisplayName === 'string'
                  ? item.initialOfferDisplayName
                  : (matchingRequest?.selectedBoxOfferDisplayNameSnapshot ?? null),
              assignmentEligibility: 'ELIGIBLE',
              availablePhysicalBoxes: Array.isArray(item.availablePhysicalBoxes)
                ? item.availablePhysicalBoxes.filter(
                    (box): box is { boxId: string; displayName: string } =>
                      isRecord(box) &&
                      typeof box.boxId === 'string' &&
                      typeof box.displayName === 'string',
                  )
                : availableBoxes.value.map((box) => ({
                    boxId: box.boxId,
                    displayName: box.displayName,
                  })),
            };
          })
      : [];
    if (isRecord(scopeBody.scopes)) {
      providerScopes.value = {
        cities: Array.isArray(scopeBody.scopes.cities)
          ? scopeBody.scopes.cities.filter((item): item is string => typeof item === 'string')
          : [],
        apartments: Array.isArray(scopeBody.scopes.apartments)
          ? scopeBody.scopes.apartments.filter(
              (item): item is string => typeof item === 'string',
            )
          : [],
        owners: Array.isArray(scopeBody.scopes.owners)
          ? scopeBody.scopes.owners.filter((item): item is string => typeof item === 'string')
          : [],
      };
    }
    initializeDrafts();
    viewStale.value = false;
  } catch (cause) {
    const loadError = new Error(
      (cause instanceof Error ? cause.message : 'Préparations indisponibles.') +
        ' Actualisez avant toute nouvelle action.',
    );
    viewStale.value = true;
    success.value = '';
    error.value = loadError.message;
    if (propagateError) throw loadError;
  } finally {
    loading.value = false;
  }
}

async function assignApartment(apartment: OperationalApartment): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  const request = receivedFor(apartment);
  const assignmentKey = `${apartment.apartment}:${apartment.slotNumber}`;
  const physicalBoxId = assignmentBoxIds.value[assignmentKey];
  const selectedBoxOfferId = request?.selectedBoxOfferId ?? apartment.initialOfferId;
  if (!physicalBoxId || !selectedBoxOfferId) {
    error.value = 'Choisissez une Box physique disponible et une offre.';
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const endpoint = request
      ? `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/assign-available`
      : `/provider/api/apartments/${encodeURIComponent(apartment.apartment)}/slots/${apartment.slotNumber}/assign-available`;
    const response = await globalThis.fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ physicalBoxId, selectedBoxOfferId }),
    });
    if (!response.ok) throw new Error('Affectation refusée.');
    success.value = `Box physique affectée à ${apartment.apartment}, sans création.`;
    await load({ propagateError: true });
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Affectation impossible.';
  } finally {
    saving.value = false;
  }
}

async function alertAdminNoEligibleBox(apartment: OperationalApartment): Promise<void> {
  const key = `${apartment.apartment}:${apartment.slotNumber}`;
  saving.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/apartments/${encodeURIComponent(apartment.apartment)}/slots/${apartment.slotNumber}/no-eligible-box-alert`,
      { method: 'POST', credentials: 'include' },
    );
    if (!response.ok) throw new Error('Alerte Admin refusée.');
    missingBoxAlerts.value[key] = true;
    success.value = `Admin alerté pour ${apartment.apartment}.`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Alerte Admin impossible.';
  } finally {
    saving.value = false;
  }
}

async function afterBoxWithdrawal(): Promise<void> {
  await load();
  if (viewStale.value) return;
  success.value =
    'Box récupérée. Dans Boxes, choisissez une offre et confirmez le nouveau contenu avant livraison.';
}

async function confirmBoxRecovered(box: ProviderBox): Promise<void> {
  if (
    !box.replacementApartmentName ||
    !globalThis.confirm(`La Box de ${box.replacementApartmentName} est-elle bien récupérée ?`)
  ) {
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/boxes/${encodeURIComponent(box.boxId)}/recovered`,
      { method: 'POST', credentials: 'include' },
    );
    if (!response.ok) throw new Error('Confirmation de récupération refusée.');
    success.value = `${box.replacementApartmentName} attend maintenant une nouvelle Box physique.`;
    await load({ propagateError: true });
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Récupération impossible.';
  } finally {
    saving.value = false;
  }
}

async function reportBoxCondition(
  box: ProviderBox,
  conditionState: 'THEFT' | 'BREAKAGE',
): Promise<void> {
  saving.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/boxes/${encodeURIComponent(box.boxId)}/condition`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ conditionState }),
      },
    );
    if (!response.ok) throw new Error('Signalement de la Box refusé.');
    success.value =
      conditionState === 'THEFT'
        ? `${box.displayName} signalée volée.`
        : `${box.displayName} signalée cassée.`;
    reportingBoxIds.value[box.boxId] = false;
    await load({ propagateError: true });
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Signalement impossible.';
  } finally {
    saving.value = false;
  }
}

function apartmentCardNumerator(apartment: string, cardKey: string): string {
  if (!apartment) return '';
  return operationalCardNumbering.value.get(cardKey) ?? '';
}

function providerExceptionResponseError(
  response: Awaited<ReturnType<typeof globalThis.fetch>>,
  fallback: string,
): Error {
  if ([401, 403].includes(response.status)) {
    return new Error('Session prestataire non autorisée pour cette demande.');
  }
  if ([400, 409].includes(response.status)) {
    return new Error('Le signalement est invalide ou ne correspond plus à l’état serveur.');
  }
  return new Error(fallback);
}

async function proposeAlternative(request: Preparation): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  const date = new Date(alternativeDates.value[request.deliveryRequestId] ?? '');
  if (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now()) {
    error.value = 'Choisissez un jour et une heure futurs.';
    return;
  }
  const note = alternativeDrafts.value[request.deliveryRequestId]?.trim() ?? '';
  const proposal = `${date.toLocaleString('fr-FR', { timeZoneName: 'short' })}${note ? ` — ${note}` : ''}`;
  if (proposal.length > 128) {
    error.value = 'Raccourcissez la précision pour conserver un horaire lisible.';
    return;
  }
  if (exceptionSubmitting.value[request.deliveryRequestId]) return;
  exceptionSubmitting.value[request.deliveryRequestId] = true;
  error.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/propose-alternative`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ proposal }),
      },
    );
    if (!response.ok) {
      throw providerExceptionResponseError(response, 'Proposition impossible.');
    }
    const responseBody: unknown = await response.json().catch(() => undefined);
    if (!isVerifiedAlternativeProjection(responseBody, request.deliveryRequestId, proposal)) {
      throw new Error(
        'Proposition non vérifiée par le serveur. Vérifiez la demande avant de réessayer.',
      );
    }
    await load({ propagateError: true });
    const matchingRequests = preparations.value.filter(
      (candidate) => candidate.deliveryRequestId === request.deliveryRequestId,
    );
    if (
      matchingRequests.length !== 1 ||
      !isVerifiedAlternativeProjection(matchingRequests[0], request.deliveryRequestId, proposal)
    ) {
      throw new Error(
        'Proposition non vérifiée après relecture. Vérifiez la demande avant de réessayer.',
      );
    }
    success.value = `Alternative transmise au gestionnaire pour ${request.apartmentName}.`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Proposition impossible.';
  } finally {
    exceptionSubmitting.value[request.deliveryRequestId] = false;
  }
}

async function refuseDelivery(request: Preparation): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  const reason = refusalDrafts.value[request.deliveryRequestId]?.trim() ?? '';
  if (reason.length < 1 || reason.length > 128) {
    error.value = 'Le motif de refus est obligatoire et limité à 128 caractères.';
    return;
  }
  if (exceptionSubmitting.value[request.deliveryRequestId]) return;
  exceptionSubmitting.value[request.deliveryRequestId] = true;
  error.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/refuse`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    );
    if (!response.ok) {
      throw providerExceptionResponseError(response, 'Refus impossible.');
    }
    await load({ propagateError: true });
    success.value = `Refus transmis au gestionnaire pour ${request.apartmentName}.`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Refus impossible.';
  } finally {
    exceptionSubmitting.value[request.deliveryRequestId] = false;
  }
}

async function confirmPreparation(request: Preparation): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  const draft = productDrafts.value[request.deliveryRequestId] ?? [];
  if (
    draft.length !== productsFor(request).length ||
    draft.some(
      (product) =>
        !productCatalog.value.some((candidate) => candidate.name === product.actualName) ||
        !Number.isSafeInteger(product.actualQuantity) ||
        product.actualQuantity < 0,
    )
  ) {
    error.value = 'Les produits et quantités de vérification sont invalides.';
    return;
  }
  saving.value = true;
  error.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/confirm-ready`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ products: draft }),
      },
    );
    if (!response.ok) throw new Error('Confirmation de préparation refusée.');
    success.value = `${request.selectedBoxOfferDisplayNameSnapshot} prête pour ${request.apartmentName}.`;
    await load({ propagateError: true });
    workflow.value =
      request.deliveryLifecycleState === 'PREPARING' && !cancellationBlocks(request)
        ? 'READY'
        : 'DELIVERED';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Confirmation impossible.';
  } finally {
    saving.value = false;
  }
}

function beginStockError(request: Preparation): void {
  modifying.value[request.deliveryRequestId] = true;
  error.value = '';
}

async function confirmDelivery(request: Preparation): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  if (!globalThis.confirm(`La Box pour ${request.apartmentName} est-elle bien livrée ?`))
    return;
  saving.value = true;
  error.value = '';
  actionError.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/confirm-delivered`,
      { method: 'POST', credentials: 'include' },
    );
    if (!response.ok) throw new Error('Confirmation de livraison refusée.');
    success.value = `Livraison confirmée pour ${request.apartmentName}.`;
    await load({ propagateError: true });
    workflow.value =
      request.deliveryLifecycleState === 'PREPARING' && !cancellationBlocks(request)
        ? 'READY'
        : 'DELIVERED';
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Confirmation impossible.';
    success.value = '';
    await load();
    const current = preparations.value.find(
      (r) => r.deliveryRequestId === request.deliveryRequestId,
    );
    actionError.value =
      !viewStale.value && current?.deliveryCapturePending
        ? 'Encaissement à vérifier avec l’administrateur. La livraison n’est pas confirmée. Consultez Alertes.'
        : message;
  } finally {
    saving.value = false;
  }
}

async function changeLockCode(): Promise<void> {
  if (
    saving.value ||
    !lockBoxId.value ||
    !/^\d{3}$/.test(newLockCode.value) ||
    !physicalLockChanged.value
  )
    return;
  saving.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/boxes/${encodeURIComponent(lockBoxId.value)}/lock-code`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ lockCode: newLockCode.value }),
      },
    );
    if (!response.ok) throw new Error('Le changement de code n’a pas été enregistré.');
    newLockCode.value = '';
    physicalLockChanged.value = false;
    await load({ propagateError: true });
    success.value = 'Code enregistré. Prochain renouvellement dans six mois.';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Changement impossible.';
  } finally {
    saving.value = false;
  }
}

async function assignReceived(request: Preparation): Promise<void> {
  if (loading.value || saving.value || viewStale.value) return;
  const physicalBoxId = assignmentBoxIds.value[request.deliveryRequestId];
  if (!physicalBoxId) return;
  saving.value = true;
  error.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(request.deliveryRequestId)}/assign-available`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ physicalBoxId, selectedBoxOfferId: request.selectedBoxOfferId }),
      },
    );
    if (!response.ok) throw new Error('Affectation refusée.');
    await load({ propagateError: true });
    workflow.value = 'PREPARING';
    success.value = 'Box affectée. Vous pouvez confirmer sa préparation.';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Affectation impossible.';
  } finally {
    saving.value = false;
  }
}
onMounted(load);
</script>

<template>
  <main id="main" class="admin-shell provider-shell mvpa-operational-shell ops-mobile-shell">
    <header class="admin-header mvpa-operational-header">
      <div class="mvpa-operational-brand" aria-label="OTEM Upsells Manager">
        <OperatorAccountMenu />
        <strong>OTEM Upsells Manager</strong>
      </div>
      <p class="eyebrow">OTEM · Interface prestataire</p>
      <h1>Espace préparateur</h1>
      <p v-if="providerId" class="provider-identity" data-testid="provider-identity">
        Session prestataire : <strong>{{ providerId }}</strong>
      </p>
      <p class="provider-scope-summary" data-testid="provider-scope-summary">
        Périmètre · villes {{ providerScopes.cities.join(', ') || 'toutes connues' }} ·
        appartements {{ providerScopes.apartments.join(', ') || 'tous connus' }} · propriétaires
        {{ providerScopes.owners.join(', ') || 'tous connus' }}
      </p>
    </header>
    <p v-if="loading" class="flow-status" role="status">Chargement des préparations…</p>
    <p v-if="error" class="flow-error" role="alert">{{ error }}</p>
    <p v-if="actionError" class="flow-error" role="alert">{{ actionError }}</p>
    <p v-if="success" class="flow-status" role="status">{{ success }}</p>

    <div class="ops-toolbar">
      <button
        class="button button-secondary"
        type="button"
        :disabled="loading || saving"
        data-testid="provider-refresh"
        @click="load()"
      >
        Actualiser
      </button>
    </div>
    <section
      v-if="activeTab === 'home'"
      class="ops-home provider-today"
      aria-label="Aujourd’hui"
    >
      <h2>À faire</h2>
      <p>Ouvrez une rubrique pour agir. Les échéances les plus proches sont en premier.</p>
      <template v-if="workflowCountsReady">
        <button
          type="button"
          class="today-stock"
          :class="{ 'today-stock-blocked': ordersWithoutBox.length > 0 }"
          data-testid="provider-today-stock"
          @click="activeTab = 'boxes'"
        >
          <strong
            >{{ availableBoxes.length }} box{{
              availableBoxes.length > 1 ? 'es' : ''
            }}
            disponible{{ availableBoxes.length > 1 ? 's' : '' }}</strong
          >
          <span v-if="ordersWithoutBox.length"
            >{{ ordersWithoutBox.length }} commande{{
              ordersWithoutBox.length > 1 ? 's' : ''
            }}
            sans box compatible · Voir les boxes →</span
          >
          <span v-else>Consulter les boxes et les récupérations →</span>
        </button>
        <details
          v-for="group in dailyGroups"
          :key="group.id"
          class="today-group"
          :class="{ 'today-has-tasks': group.requests.length > 0 }"
          :data-testid="`provider-today-${group.id}`"
        >
          <summary>
            <span>{{ group.label }}</span
            ><strong>({{ group.requests.length }})</strong>
          </summary>
          <p class="today-hint">{{ group.hint }}</p>
          <p v-if="!group.requests.length" class="today-hint">
            Rien à traiter dans cette rubrique.
          </p>
          <article
            v-for="request in group.requests"
            :key="request.deliveryRequestId"
            class="today-order"
            :data-testid="`provider-today-order-${request.deliveryRequestId}`"
          >
            <OrderReference :order-id="request.deliveryRequestId" />
            <strong>{{ request.apartmentName }} · {{ request.travelerName }}</strong>
            <p>{{ request.selectedBoxOfferDisplayNameSnapshot }}</p>
            <p>
              Échéance :
              <time :datetime="request.deliveryDeadlineAt">{{
                formatDeliveryDeadline(request.deliveryDeadlineAt)
              }}</time>
            </p>
            <p
              v-if="
                request.deliveryLifecycleState === 'RECEIVED' &&
                !request.availablePhysicalBoxes?.length
              "
              class="today-blocked"
            >
              Box compatible indisponible
            </p>
            <button type="button" class="button button-primary" @click="openWorkflow(group.id)">
              Ouvrir {{ group.id === 'ALERTS' ? 'la demande' : 'les commandes' }}
            </button>
          </article>
        </details>
      </template>
      <p v-else role="status">Actualisez pour afficher les actions à jour.</p>
    </section>
    <div
      ref="workflowNavigation"
      v-if="activeTab === 'orders'"
      class="ops-filters"
      aria-label="Étapes des commandes"
    >
      <button
        v-for="step in workflowSteps"
        :key="step.id"
        type="button"
        :aria-pressed="workflow === step.id"
        :data-testid="`provider-step-${step.id}`"
        :class="{
          'workflow-has-tasks':
            workflowCountsReady && step.count > 0 && step.id !== 'DELIVERED',
          'workflow-clear':
            workflowCountsReady && (step.count === 0 || step.id === 'DELIVERED'),
        }"
        @click="workflow = step.id"
      >
        {{ step.label }}
        <span class="workflow-count">{{
          workflowCountsReady ? `(${step.count})` : '(…)'
        }}</span>
      </button>
    </div>
    <section
      v-if="!loading && !viewStale && !error && activeTab === 'orders' && workflow === 'ALERTS'"
      aria-label="Annulations clients"
    >
      <article
        v-for="request in preparations.filter(
          (r) =>
            needsAttention(r) ||
            r.modifications?.some(
              (m) =>
                workflow === 'ALERTS' ||
                !['APPLIED', 'REJECTED'].includes(m.status) ||
                (m.status === 'REJECTED' &&
                  !['DELIVERED', 'CANCELLED'].includes(r.deliveryLifecycleState)),
            ) ||
            (r.cancellation &&
              (workflow === 'ALERTS' ||
                !['REJECTED', 'REFUNDED_SIMULATED', 'REFUNDED', 'SETTLED'].includes(
                  r.cancellation.status,
                ))),
        )"
        :key="request.deliveryRequestId"
      >
        <OrderReference :order-id="request.deliveryRequestId" />
        <h2>{{ request.apartmentName }} · {{ request.selectedBoxOfferDisplayNameSnapshot }}</h2>
        <p v-if="request.travelerComment" class="order-comment">
          <strong>Commentaire client :</strong> {{ request.travelerComment }}
        </p>
        <TravelerCommentReview
          :order-id="request.deliveryRequestId"
          :comment="request.travelerComment"
          :review="request.travelerCommentReview"
          :read-only="['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)"
          :disabled="saving || viewStale || cancellationBlocks(request)"
          @changed="load()"
        />
        <OrderManagement
          role="provider"
          :order="request"
          :disabled="saving || viewStale"
          @changed="load()"
        />
      </article>
    </section>
    <section
      v-if="activeTab === 'catalog' && !loading && !viewStale && !error"
      class="provider-zone"
      aria-label="Catalogue des offres"
    >
      <h2>Catalogue de packs</h2>
      <p>
        Chaque offre décrit un pack de produits. Elle peut être commandée plusieurs fois selon
        les stocks. Les numéros identifient les boxes physiques.
      </p>
      <OfferGallery
        v-if="!selectedCatalogOffer"
        :offers="offers"
        :show-prices="false"
        :orders="preparations"
        @select="selectedOfferId = $event"
      />
      <article v-else class="provider-offer-detail">
        <button class="button button-secondary" type="button" @click="selectedOfferId = ''">
          ← Toutes les offres
        </button>
        <h3>{{ selectedCatalogOffer.displayName }}</h3>
        <img
          v-if="selectedCatalogOffer.photoDataUrl"
          :src="selectedCatalogOffer.photoDataUrl"
          :alt="selectedCatalogOffer.displayName"
          style="width: 100%; border-radius: 1rem"
        />
        <h4>Composition</h4>
        <ul v-if="selectedCatalogOffer.products?.length">
          <li v-for="product in selectedCatalogOffer.products" :key="product.name">
            {{ product.name }} · {{ product.quantity }}
          </li>
        </ul>
        <p v-else>Composition à renseigner par l’administrateur.</p>
      </article>
    </section>
    <fieldset class="ops-content" :disabled="loading || saving || viewStale">
      <legend class="sr-only">Actions préparateur</legend>
      <section v-if="activeTab === 'more'" aria-label="Créer et gérer">
        <ManualPreparation
          :options="manualPreparationOptions"
          :disabled="loading || saving || viewStale"
          @created="afterManualPreparation"
        />
        <BoxSetupTutorial>
          <template #box-settings>
            <section data-testid="provider-lock-renewal">
              <p>
                Réglez d’abord le cadenas physique, puis enregistrez son nouveau code. Le QR
                Code reste identique.
              </p>
              <form class="provider-lock-form" @submit.prevent="changeLockCode">
                <ImmediateChoice
                  id="provider-lock-box"
                  label="Box"
                  v-model="lockBoxId"
                  placeholder="Choisir une Box"
                  :options="
                    boxes
                      .filter((b) => b.conditionState === 'ACTIVE')
                      .map((b) => ({
                        value: b.boxId,
                        label:
                          b.displayName +
                          (b.lockRenewal?.renewalDue ? ' — renouvellement à effectuer' : ''),
                      }))
                  "
                  :disabled="loading || saving || viewStale"
                />
                <p v-if="boxes.find((box) => box.boxId === lockBoxId)?.lockRenewal?.renewalAt">
                  Prochaine échéance :
                  {{
                    new Date(
                      boxes.find((box) => box.boxId === lockBoxId)!.lockRenewal!.renewalAt!,
                    ).toLocaleDateString('fr-FR')
                  }}
                </p>
                <ProviderBoxAccess
                  v-if="!loading && !viewStale && boxes.find((box) => box.boxId === lockBoxId)"
                  :box="boxes.find((box) => box.boxId === lockBoxId)!"
                />
                <label for="provider-new-lock-code">Nouveau code à trois chiffres</label>
                <input
                  id="provider-new-lock-code"
                  v-model="newLockCode"
                  inputmode="numeric"
                  pattern="[0-9]{3}"
                  minlength="3"
                  maxlength="3"
                  autocomplete="off"
                  required
                />
                <label class="provider-lock-confirm"
                  ><input v-model="physicalLockChanged" type="checkbox" required /><span
                    >J’ai réglé le cadenas physique sur ce nouveau code.</span
                  ></label
                >
                <button
                  class="button button-primary"
                  type="submit"
                  :disabled="
                    saving ||
                    !physicalLockChanged ||
                    !lockBoxId ||
                    !/^[0-9]{3}$/.test(newLockCode)
                  "
                >
                  Enregistrer le code
                </button>
              </form>
            </section>
          </template>
        </BoxSetupTutorial>
      </section>

      <section
        v-show="activeTab === 'boxes'"
        class="provider-zone"
        aria-labelledby="provider-apartments-title"
      >
        <div class="admin-section-header">
          <div>
            <p class="admin-section-kicker">Périmètre opérationnel</p>
            <h2 id="provider-apartments-title">Appartements &amp; Boxes installées</h2>
          </div>
          <span class="admin-count-badge">{{ operationalCardCount }}</span>
        </div>
        <p v-if="!loading && operationalScopeEmpty" class="flow-status">
          Aucune Box installée ni appartement à équiper.
        </p>
        <div class="provider-apartment-grid">
          <article
            v-for="box in installedBoxes"
            :key="`position-${box.boxId}`"
            class="provider-apartment-card"
            :class="installedApartmentClass(box)"
            :data-apartment-status="
              isConfiguredInstalledBox(box) ? 'INSTALLED_NOT_ORDERED' : 'ORDERED_DELIVERED'
            "
            :data-position-identity="`physical-box:${box.boxId}`"
          >
            <strong data-testid="provider-installed-apartment">{{
              `${box.currentDeliveryAssignment?.apartmentName ?? ''}${apartmentCardNumerator(
                box.currentDeliveryAssignment?.apartmentName ?? '',
                `position:${box.boxId}`,
              )}`
            }}</strong>
            <span>{{ box.displayName }}</span>
            <ProviderBoxAccess v-if="!loading && !viewStale" :box="box" />
            <span>{{ installedApartmentStatus(box) }}</span>
            <BoxWithdrawal
              :box-id="box.boxId"
              :request-id="box.withdrawalRequestId"
              role="provider"
              :disabled="saving || loading || viewStale"
              @changed="afterBoxWithdrawal"
            />
          </article>
          <article
            v-for="box in inProgressBoxes"
            :key="`position-${box.boxId}`"
            class="provider-apartment-card provider-apartment-in-progress"
            data-testid="provider-apartment-in-progress"
            :data-position-identity="`physical-box:${box.boxId}`"
          >
            <strong>
              {{
                `${box.currentDeliveryAssignment?.apartmentName ?? ''}${apartmentCardNumerator(
                  box.currentDeliveryAssignment?.apartmentName ?? '',
                  `position:${box.boxId}`,
                )}`
              }}
            </strong>
            <span>{{ box.displayName }}</span>
            <ProviderBoxAccess v-if="!loading && !viewStale" :box="box" />
            <span>
              {{
                box.currentDeliveryAssignment?.deliveryLifecycleState === 'READY'
                  ? 'Prête à livrer'
                  : 'Préparation en cours'
              }}
            </span>
          </article>
          <article
            v-for="apartment in apartmentsWithoutActiveBox"
            :key="`${apartment.apartment}:${apartment.slotNumber}`"
            class="provider-apartment-card provider-apartment-unassigned"
            data-testid="provider-apartment-without-box"
            :data-position-identity="`planned-slot:${apartment.apartment}:${apartment.slotNumber}`"
          >
            <strong>
              {{
                `${apartment.apartment}${apartmentCardNumerator(
                  apartment.apartment,
                  `position:${apartment.apartment}:${apartment.slotNumber}`,
                )}`
              }}
            </strong>
            <span>Aucune Box active</span>
            <p v-if="apartment.initialOfferDisplayName" class="provider-initial-offer">
              <strong>Offre initiale à préparer</strong>
              <span>{{ apartment.initialOfferDisplayName }}</span>
            </p>
            <template
              v-if="
                apartment.initialOfferId &&
                apartment.assignmentEligibility === 'ELIGIBLE' &&
                eligibleBoxesFor(apartment).length > 0
              "
            >
              <label :for="`assignment-box-${apartment.apartment}-${apartment.slotNumber}`">
                Box physique disponible
              </label>
              <select
                :id="`assignment-box-${apartment.apartment}-${apartment.slotNumber}`"
                v-model="assignmentBoxIds[`${apartment.apartment}:${apartment.slotNumber}`]"
                required
              >
                <option value="" disabled>Choisir une Box physique</option>
                <option
                  v-for="box in eligibleBoxesFor(apartment)"
                  :key="box.boxId"
                  :value="box.boxId"
                >
                  {{ box.displayName }}
                </option>
              </select>
              <button
                class="button button-assign"
                type="button"
                :disabled="saving"
                :data-testid="`provider-assign-${apartment.apartment}-${apartment.slotNumber}`"
                @click="assignApartment(apartment)"
              >
                Affecter
              </button>
            </template>
            <div
              v-else-if="apartment.initialOfferId"
              class="provider-preparation-status-pending"
              data-testid="provider-assignment-no-eligible-box"
            >
              <span>Aucune Box physique éligible pour cette demande.</span>
              <button
                class="button button-danger"
                type="button"
                :disabled="
                  saving || missingBoxAlerts[`${apartment.apartment}:${apartment.slotNumber}`]
                "
                :data-testid="`provider-alert-admin-${apartment.apartment}-${apartment.slotNumber}`"
                @click="alertAdminNoEligibleBox(apartment)"
              >
                {{
                  missingBoxAlerts[`${apartment.apartment}:${apartment.slotNumber}`]
                    ? 'Admin alerté'
                    : 'Alerter l’admin'
                }}
              </button>
            </div>
            <span v-else class="provider-preparation-status-pending">À préparer</span>
          </article>
          <article
            v-for="box in replacementBoxes"
            :key="`position-${box.boxId}`"
            class="provider-apartment-card provider-apartment-replacement"
            :data-position-identity="`physical-box:${box.boxId}`"
          >
            <strong>
              {{
                `${box.replacementApartmentName}${apartmentCardNumerator(
                  box.replacementApartmentName ?? '',
                  `position:${box.boxId}`,
                )}`
              }}
            </strong>
            <span>{{ box.displayName }}</span>
            <ProviderBoxAccess v-if="!loading && !viewStale" :box="box" />
            <span>Box vendue · commande consommée</span>
            <span v-if="box.conditionState === 'THEFT'">Vol</span>
            <span v-else-if="box.conditionState === 'BREAKAGE'">Casse</span>
            <span v-else>Remplacement à prévoir</span>
            <button
              v-if="box.conditionState === 'ACTIVE' && !reportingBoxIds[box.boxId]"
              class="button button-assign"
              type="button"
              :disabled="saving"
              :data-testid="`provider-box-recovered-${box.boxId}`"
              @click="confirmBoxRecovered(box)"
            >
              Box récupérée ?
            </button>
            <button
              v-if="box.conditionState === 'ACTIVE' && !reportingBoxIds[box.boxId]"
              class="button button-danger"
              type="button"
              :disabled="saving"
              :data-testid="`provider-report-${box.boxId}`"
              @click="reportingBoxIds[box.boxId] = true"
            >
              Signaler
            </button>
            <div
              v-if="box.conditionState === 'ACTIVE' && reportingBoxIds[box.boxId]"
              class="provider-report-actions"
            >
              <button
                class="button button-danger"
                type="button"
                :disabled="saving"
                :data-testid="`provider-report-theft-${box.boxId}`"
                @click="reportBoxCondition(box, 'THEFT')"
              >
                Vol
              </button>
              <button
                class="button button-danger"
                type="button"
                :disabled="saving"
                :data-testid="`provider-report-breakage-${box.boxId}`"
                @click="reportBoxCondition(box, 'BREAKAGE')"
              >
                Casse
              </button>
            </div>
          </article>
        </div>
      </section>

      <section
        v-show="activeTab === 'boxes' && availableBoxes.length > 0"
        class="provider-zone"
        aria-labelledby="provider-available-title"
      >
        <h2 id="provider-available-title">Boxes disponibles</h2>
        <div class="provider-apartment-grid">
          <article
            v-for="box in availableBoxes"
            :key="box.boxId"
            class="provider-apartment-card"
            :data-testid="`provider-available-box-${box.boxId}`"
          >
            <strong>{{ box.displayName }}</strong>
            <ProviderBoxAccess v-if="!loading && !viewStale" :box="box" />
          </article>
        </div>
      </section>

      <section
        v-show="activeTab === 'orders' && ['RECEIVED', 'ALERTS'].includes(workflow)"
        class="provider-zone"
        aria-labelledby="provider-delivery-requests-title"
      >
        <div class="admin-section-header">
          <div>
            <p class="admin-section-kicker">Demandes à confirmer</p>
            <h2 id="provider-delivery-requests-title">
              {{ workflow === 'ALERTS' ? 'Alertes et blocages' : 'Demandes reçues' }}
            </h2>
          </div>
          <span class="admin-count-badge">{{
            workflow === 'ALERTS' ? exceptionPreparations.length : receivedPreparations.length
          }}</span>
        </div>
        <button
          v-if="workflow === 'ALERTS'"
          type="button"
          class="ops-alert"
          @click="activeTab = 'boxes'"
        >
          Vérifier les logements et les Boxes disponibles →
        </button>
        <p
          v-if="workflow === 'RECEIVED' && !loading && receivedPreparations.length === 0"
          class="flow-status"
        >
          Aucune nouvelle demande à confirmer.
        </p>
        <div
          class="provider-preparation-list"
          role="group"
          aria-label="Demandes de livraison à confirmer"
        >
          <article
            v-for="request in receivedPreparations"
            v-show="workflow === 'RECEIVED'"
            :key="`exception-${request.deliveryRequestId}`"
            class="admin-delivery-card provider-delivery-exception-card"
            :data-testid="`provider-delivery-request-${request.deliveryRequestId}`"
          >
            <p class="admin-delivery-phase">À confirmer — {{ request.apartmentName }}</p>
            <OrderReference :order-id="request.deliveryRequestId" />
            <h3>{{ request.selectedBoxOfferDisplayNameSnapshot }}</h3>
            <p>{{ request.travelerName }}</p>
            <p>
              <strong>Échéance serveur :</strong>
              <time
                :datetime="request.deliveryDeadlineAt"
                :data-testid="`provider-deadline-${request.deliveryRequestId}`"
              >
                {{ formatDeliveryDeadline(request.deliveryDeadlineAt) }}
              </time>
            </p>
            <ReservationSummary :reservation="request.reservation" />
            <p v-if="request.travelerComment" class="order-comment">
              <strong>Commentaire client :</strong> {{ request.travelerComment }}
            </p>
            <TravelerCommentReview
              :key="request.deliveryRequestId"
              :order-id="request.deliveryRequestId"
              :comment="request.travelerComment"
              :review="request.travelerCommentReview"
              :read-only="['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)"
              :disabled="saving || loading || viewStale || cancellationBlocks(request)"
              @changed="load()"
            />
            <form class="ops-assign" @submit.prevent="assignReceived(request)">
              <label :for="`assign-${request.deliveryRequestId}`">Box à préparer</label>
              <select
                :id="`assign-${request.deliveryRequestId}`"
                v-model="assignmentBoxIds[request.deliveryRequestId]"
                :disabled="!request.availablePhysicalBoxes?.length"
                required
              >
                <option value="">Choisir une Box</option>
                <option
                  v-for="box in request.availablePhysicalBoxes ?? []"
                  :key="box.boxId"
                  :value="box.boxId"
                >
                  {{ box.displayName }}
                </option>
              </select>
              <button
                type="submit"
                class="button button-primary"
                :disabled="!assignmentBoxIds[request.deliveryRequestId]"
              >
                Affecter et préparer
              </button>
              <div
                v-if="!request.availablePhysicalBoxes?.length"
                class="today-blocked"
                :data-testid="`provider-no-box-${request.deliveryRequestId}`"
              >
                <p>
                  Aucune box compatible disponible. Une box déjà livrée reste affectée jusqu’à
                  sa récupération. Contactez l’administrateur si une nouvelle box doit être
                  ajoutée.
                </p>
                <button
                  type="button"
                  class="button button-secondary"
                  @click="activeTab = 'boxes'"
                >
                  Voir les boxes et récupérations
                </button>
              </div>
            </form>
            <p class="order-comment-hint">
              Le commentaire transmis crée une alerte dans l’interface administrateur.
            </p>
            <form
              class="provider-delivery-exception-form"
              :data-testid="`provider-alternative-form-${request.deliveryRequestId}`"
              @submit.prevent="proposeAlternative(request)"
            >
              <label :for="`provider-alternative-date-${request.deliveryRequestId}`"
                >Jour et heure proposés (heure de votre appareil)</label
              >
              <input
                :id="`provider-alternative-date-${request.deliveryRequestId}`"
                v-model="alternativeDates[request.deliveryRequestId]"
                type="datetime-local"
                required
                :disabled="exceptionSubmitting[request.deliveryRequestId]"
              />
              <label :for="`provider-alternative-${request.deliveryRequestId}`"
                >Précision sur la proposition (facultatif)</label
              >
              <input
                :id="`provider-alternative-${request.deliveryRequestId}`"
                v-model="alternativeDrafts[request.deliveryRequestId]"
                maxlength="64"
                :disabled="exceptionSubmitting[request.deliveryRequestId]"
              />
              <button
                class="button button-assign"
                type="submit"
                :disabled="exceptionSubmitting[request.deliveryRequestId]"
                :data-testid="`provider-propose-alternative-${request.deliveryRequestId}`"
              >
                {{
                  exceptionSubmitting[request.deliveryRequestId]
                    ? 'Transmission…'
                    : 'Transmettre à l’administrateur'
                }}
              </button>
            </form>
            <form
              class="provider-delivery-exception-form"
              :data-testid="`provider-refusal-form-${request.deliveryRequestId}`"
              @submit.prevent="refuseDelivery(request)"
            >
              <label :for="`provider-refusal-${request.deliveryRequestId}`">
                Motif du refus
              </label>
              <textarea
                :id="`provider-refusal-${request.deliveryRequestId}`"
                v-model="refusalDrafts[request.deliveryRequestId]"
                maxlength="128"
                required
                :disabled="exceptionSubmitting[request.deliveryRequestId]"
              />
              <button
                class="button button-danger"
                type="submit"
                :disabled="exceptionSubmitting[request.deliveryRequestId]"
                :data-testid="`provider-refuse-${request.deliveryRequestId}`"
              >
                {{
                  exceptionSubmitting[request.deliveryRequestId] ? 'Transmission…' : 'Refuser'
                }}
              </button>
            </form>
          </article>
        </div>
        <div
          v-if="exceptionPreparations.length"
          class="provider-preparation-list"
          role="group"
          aria-label="Exceptions de livraison transmises"
        >
          <article
            v-for="request in exceptionPreparations"
            v-show="workflow === 'ALERTS'"
            :key="`reported-exception-${request.deliveryRequestId}`"
            class="admin-delivery-card provider-delivery-exception-card"
            :data-testid="`provider-reported-exception-${request.deliveryRequestId}`"
          >
            <p class="admin-delivery-phase">
              {{
                request.deliveryLifecycleState === 'MODIFICATION_PROPOSEE'
                  ? 'Modification proposée'
                  : 'Demande refusée'
              }}
            </p>
            <OrderReference :order-id="request.deliveryRequestId" />
            <h3>{{ request.apartmentName }}</h3>
            <p>
              <strong>Échéance serveur :</strong>
              <time :datetime="request.deliveryDeadlineAt">
                {{ formatDeliveryDeadline(request.deliveryDeadlineAt) }}
              </time>
            </p>
            <p v-if="request.providerException?.proposedAlternative">
              <strong>Alternative :</strong> {{ request.providerException.proposedAlternative }}
            </p>
            <p v-if="request.providerException?.refusalReason">
              <strong>Motif :</strong> {{ request.providerException.refusalReason }}
            </p>
            <p class="flow-status" role="status">Transmis au gestionnaire.</p>
          </article>
        </div>
      </section>

      <section
        v-show="activeTab === 'orders' && workflow === 'PREPARING'"
        class="provider-zone"
        aria-labelledby="provider-preparations-title"
      >
        <div class="admin-section-header">
          <div>
            <p class="admin-section-kicker">À traiter</p>
            <h2 id="provider-preparations-title">Boxes à préparer</h2>
          </div>
          <span class="admin-count-badge">{{ activePreparations.length }}</span>
        </div>
        <p v-if="!loading && activePreparations.length === 0" class="flow-status">
          Aucune préparation à traiter.
        </p>
        <div class="provider-preparation-list" role="group" aria-label="Préparations à traiter">
          <article
            v-for="request in activePreparations"
            :key="request.deliveryRequestId"
            class="admin-delivery-card provider-preparation-card provider-preparation-card-pending"
            :data-testid="`provider-preparation-${request.deliveryRequestId}`"
          >
            <div class="provider-preparation-overview">
              <div class="provider-preparation-copy">
                <p
                  class="admin-delivery-phase provider-preparation-status-pending"
                  :data-testid="`provider-preparation-status-${request.deliveryRequestId}`"
                >
                  À préparer — {{ request.apartmentName }}
                </p>
                <OrderReference :order-id="request.deliveryRequestId" />
                <h2>{{ request.selectedBoxOfferDisplayNameSnapshot }}</h2>
                <ReservationSummary :reservation="request.reservation" />
                <p v-if="request.travelerComment" class="order-comment">
                  <strong>Commentaire client :</strong> {{ request.travelerComment }}
                </p>
                <TravelerCommentReview
                  :key="request.deliveryRequestId"
                  :order-id="request.deliveryRequestId"
                  :comment="request.travelerComment"
                  :review="request.travelerCommentReview"
                  :read-only="
                    ['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)
                  "
                  :disabled="saving || loading || viewStale || cancellationBlocks(request)"
                  @changed="load()"
                />
                <p><strong>Appartement :</strong> {{ request.apartmentName }}</p>
                <p><strong>Voyageur :</strong> {{ request.travelerName }}</p>
                <p>
                  <strong>Échéance serveur :</strong>
                  <time :datetime="request.deliveryDeadlineAt">
                    {{ formatDeliveryDeadline(request.deliveryDeadlineAt) }}
                  </time>
                </p>
                <p v-if="request.assignedBox">
                  <strong>Box physique :</strong> {{ request.assignedBox.displayName }}
                </p>
                <ProviderBoxAccess
                  v-if="currentBoxFor(request)"
                  :box="currentBoxFor(request)!"
                />
              </div>
              <figure
                class="provider-preparation-visual"
                :data-testid="`provider-preparation-image-${request.deliveryRequestId}`"
              >
                <img
                  :src="offerPhotoFor(request)"
                  :alt="`Box ${request.selectedBoxOfferDisplayNameSnapshot} à préparer pour ${request.apartmentName}`"
                />
                <figcaption>Commande à préparer</figcaption>
              </figure>
            </div>
            <p v-if="modifying[request.deliveryRequestId]" class="order-comment-hint">
              Adaptez les produits en cas de problème de stock ou à la demande du client.
              L’administrateur pourra consulter la modification.
            </p>
            <div
              v-if="(productDrafts[request.deliveryRequestId] ?? []).length"
              class="provider-product-columns"
              role="group"
              aria-label="Préparation et vérification"
            >
              <template
                v-for="(product, index) in productDrafts[request.deliveryRequestId] ?? []"
                :key="product.expectedName"
              >
                <div class="provider-product-expected">
                  <span>{{ product.expectedName }}</span>
                  <strong>{{ product.expectedQuantity }}</strong>
                </div>
                <div class="provider-product-verification">
                  <label :for="`provider-product-${request.deliveryRequestId}-${index}`">
                    Produit
                  </label>
                  <select
                    :id="`provider-product-${request.deliveryRequestId}-${index}`"
                    v-model="product.actualName"
                    :disabled="!modifying[request.deliveryRequestId]"
                  >
                    <option
                      v-for="catalogProduct in productCatalog"
                      :key="catalogProduct.name"
                      :value="catalogProduct.name"
                    >
                      {{ catalogProduct.name }}
                    </option>
                  </select>
                  <label :for="verificationInputId(request.deliveryRequestId, index)">
                    Quantité vérifiée
                  </label>
                  <input
                    :id="verificationInputId(request.deliveryRequestId, index)"
                    v-model.number="product.actualQuantity"
                    type="number"
                    min="0"
                    max="999"
                    inputmode="numeric"
                  />
                </div>
              </template>
            </div>
            <div class="provider-preparation-actions">
              <button
                v-if="!modifying[request.deliveryRequestId]"
                class="button button-danger"
                type="button"
                :disabled="saving"
                :data-testid="`provider-stock-error-${request.deliveryRequestId}`"
                @click="beginStockError(request)"
              >
                Modifier
              </button>
              <button
                v-if="!modifying[request.deliveryRequestId]"
                class="button button-confirm"
                type="button"
                :disabled="saving || commentBlocks(request)"
                :data-testid="`provider-confirm-preparation-${request.deliveryRequestId}`"
                @click="confirmPreparation(request)"
              >
                Confirmer la préparation
              </button>
              <button
                v-else
                class="button button-assign"
                type="button"
                :disabled="saving || commentBlocks(request)"
                :data-testid="`provider-confirm-product-modification-${request.deliveryRequestId}`"
                @click="confirmPreparation(request)"
              >
                Confirmer modification produits
              </button>
            </div>
          </article>
        </div>
      </section>

      <section
        v-show="activeTab === 'orders' && workflow === 'READY'"
        class="provider-zone"
        aria-labelledby="provider-ready-title"
      >
        <div class="admin-section-header">
          <div>
            <p class="admin-section-kicker">Livraison explicite</p>
            <h2 id="provider-ready-title">Boxes prêtes à livrer</h2>
          </div>
          <span class="admin-count-badge">{{ readyDeliveries.length }}</span>
        </div>
        <p v-if="!loading && readyDeliveries.length === 0" class="flow-status">
          Aucune Box en attente de confirmation de livraison.
        </p>
        <article
          v-for="request in readyDeliveries"
          :key="request.deliveryRequestId"
          class="provider-ready-card"
        >
          <div>
            <OrderReference :order-id="request.deliveryRequestId" />
            <strong>{{ request.apartmentName }}</strong>
            <small>{{ request.travelerName }}</small>
            <span>{{ request.selectedBoxOfferDisplayNameSnapshot }}</span>
            <small> Échéance {{ formatDeliveryDeadline(request.deliveryDeadlineAt) }} </small>
            <small
              v-if="request.temporaryPreparation"
              data-testid="provider-temporary-preparation"
            >
              {{ request.temporaryPreparation.displayName }}
            </small>
            <ReservationSummary :reservation="request.reservation" />
            <p v-if="request.travelerComment" class="order-comment">
              <strong>Commentaire client :</strong> {{ request.travelerComment }}
            </p>
            <TravelerCommentReview
              :key="request.deliveryRequestId"
              :order-id="request.deliveryRequestId"
              :comment="request.travelerComment"
              :review="request.travelerCommentReview"
              :read-only="['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)"
              :disabled="saving || loading || viewStale || cancellationBlocks(request)"
              @changed="load()"
            />
            <small v-if="request.assignedBox">{{ request.assignedBox.displayName }}</small>
            <ProviderBoxAccess v-if="currentBoxFor(request)" :box="currentBoxFor(request)!" />
          </div>
          <button
            class="button button-primary"
            type="button"
            :disabled="saving || commentBlocks(request)"
            :data-testid="`provider-confirm-delivered-${request.deliveryRequestId}`"
            @click="confirmDelivery(request)"
          >
            Box livrée ?
          </button>
        </article>
      </section>
      <section
        v-show="activeTab === 'orders' && workflow === 'DELIVERED'"
        class="provider-zone"
      >
        <h2>Commandes livrées</h2>
        <article
          v-for="request in preparations.filter(
            (item) => item.deliveryLifecycleState === 'DELIVERED',
          )"
          :key="request.deliveryRequestId"
          class="ops-order-row"
        >
          <OrderReference :order-id="request.deliveryRequestId" />
          <strong>{{ request.apartmentName }}</strong
          ><span class="ops-tag ops-done">Terminé</span>
          <p>{{ request.travelerName }}</p>
          <ReservationSummary :reservation="request.reservation" />
          <p v-if="request.travelerComment" class="order-comment">
            <strong>Commentaire client :</strong> {{ request.travelerComment }}
          </p>
          <TravelerCommentReview
            :key="request.deliveryRequestId"
            :order-id="request.deliveryRequestId"
            :comment="request.travelerComment"
            :review="request.travelerCommentReview"
            :read-only="['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)"
            :disabled="saving || loading || viewStale || cancellationBlocks(request)"
            @changed="load()"
          />
          <p>
            {{ request.selectedBoxOfferDisplayNameSnapshot }} ·
            {{ request.assignedBox?.displayName }}
          </p>
          <ProviderBoxAccess v-if="currentBoxFor(request)" :box="currentBoxFor(request)!" />
          <BoxWithdrawal
            v-for="box in boxes.filter(
              (b) => b.withdrawalRequestId === request.deliveryRequestId,
            )"
            :key="box.boxId"
            :box-id="box.boxId"
            :request-id="box.withdrawalRequestId"
            role="provider"
            :disabled="saving || loading || viewStale"
            @changed="afterBoxWithdrawal"
          />
        </article>
        <p v-if="!preparations.some((item) => item.deliveryLifecycleState === 'DELIVERED')">
          Aucune livraison terminée.
        </p>
      </section>
    </fieldset>
    <OperationalNavigation :active="activeTab" :items="navigation" @select="selectNavigation" />
  </main>
</template>

<style scoped>
.today-stock {
  display: grid;
  gap: 0.45rem;
  width: 100%;
  padding: 1rem;
  margin: 1rem 0;
  border: 1px solid #bbd2c3;
  border-radius: 14px;
  background: #eef8f1;
  color: #14532d;
  text-align: left;
  font: inherit;
}
.today-stock-blocked {
  background: #fef9c3;
  color: #713f12;
  border-color: #eab308;
}
.today-group {
  margin: 0.8rem 0;
  border: 1px solid #dce5df;
  border-left: 5px solid #15803d;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}
.today-has-tasks {
  border-left-color: #b91c1c;
}
.today-group summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.7rem;
  min-height: 56px;
  padding: 0.85rem;
  cursor: pointer;
  font-weight: 700;
  color: #14532d;
}
.today-group summary::after {
  content: '+';
  font-size: 1.35rem;
}
.today-group[open] summary::after {
  content: '−';
}
.today-group summary span {
  flex: 1;
}
.today-group summary strong {
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  background: #166534;
  color: #fff;
}
.today-has-tasks summary {
  color: #991b1b;
}
.today-has-tasks summary strong {
  background: #b91c1c;
}
.today-hint {
  padding: 0 0.85rem;
  font-size: 0.9rem;
}
.today-order {
  margin: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid #e2e8f0;
  overflow-wrap: anywhere;
}
.today-order p {
  margin: 0.45rem 0;
}
.today-order .button {
  width: 100%;
  margin-top: 0.5rem;
}
.today-blocked {
  color: #991b1b;
  font-weight: 600;
}
.today-group summary:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: -3px;
}

.ops-filters {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 2px;
}
.ops-filters button {
  min-width: 0;
  padding: 8px 2px;
  font-size: clamp(11px, 3vw, 13px);
  white-space: nowrap;
}
.workflow-count {
  display: block;
  width: max-content;
  min-width: 1.7em;
  margin: 0.25rem auto 0;
  padding: 0.12rem 0.3rem;
  border-radius: 999px;
  font-weight: 800;
  line-height: 1.3;
  background: #e2e8f0;
  color: #334155;
}
.workflow-has-tasks .workflow-count {
  background: #b91c1c;
  color: #fff;
}
.workflow-clear .workflow-count {
  background: #166534;
  color: #fff;
}
</style>
